import React, { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import FeatureGate from "@/components/access/FeatureGate";
import FullScreenLoader from "@/components/ui/FullScreenLoader";
import Skeleton from "@/components/ui/Skeleton";
import { useSubscription } from "@/hooks/useSubscription";
import DashboardLayout from "@/layouts/DashboardLayout";
import { LockedFeatureKey } from "@/constants/lockedFeatures";
import { SubscriptionFeature } from "@/types/auth";
import { ProtectedRoute, PublicOnlyRoute, RequireRole } from "./guards";
import { PATHS } from "./paths";

// Páginas carregadas sob demanda (o editor da vitrine, mapas e gráficos são
// pesados e não precisam entrar no bundle inicial).
const Login = lazy(() => import("@/pages/auth/Login"));
const Cadastro = lazy(() => import("@/pages/auth/Cadastro"));
const EsqueciSenha = lazy(() => import("@/pages/auth/EsqueciSenha"));
const NovaSenha = lazy(() => import("@/pages/auth/NovaSenha"));
const PagamentoConfirmado = lazy(() => import("@/pages/auth/PagamentoConfirmado"));
const OAuthCallback = lazy(() => import("@/pages/auth/OAuthCallback"));
const GoogleLoginCallback = lazy(() => import("@/pages/auth/GoogleLoginCallback"));
const ContratarConta = lazy(() => import("@/pages/auth/ContratarConta"));

const Inicio = lazy(() => import("@/pages/Inicio"));
const VisaoGeral = lazy(() => import("@/pages/painel/VisaoGeral"));
const GeradorLink = lazy(() => import("@/pages/painel/GeradorLink"));

const SeuSite = lazy(() => import("@/pages/analytics/SeuSite"));
const GoogleAds = lazy(() => import("@/pages/analytics/GoogleAds"));
const MetaAds = lazy(() => import("@/pages/analytics/MetaAds"));
const Tags = lazy(() => import("@/pages/analytics/Tags"));
const TagsCanal = lazy(() => import("@/pages/analytics/TagsCanal"));
const TagDetalhe = lazy(() => import("@/pages/analytics/TagDetalhe"));
const FunilRelatorios = lazy(() => import("@/pages/analytics/FunilRelatorios"));
const FunilFormularios = lazy(() => import("@/pages/analytics/FunilFormularios"));

const VitrineInicio = lazy(() => import("@/pages/social/Inicio"));
const VitrineCabecalho = lazy(() => import("@/pages/social/Cabecalho"));
const VitrineEditor = lazy(() => import("@/pages/social/Editor"));
const VitrineTemas = lazy(() => import("@/pages/social/Temas"));
const VitrineRelatorios = lazy(() => import("@/pages/social/Relatorios"));
const VitrinePublica = lazy(() => import("@/pages/social/VitrinePublica"));

const CodigoMonitoramento = lazy(() => import("@/pages/integracoes/CodigoMonitoramento"));
const ContasIntegradas = lazy(() => import("@/pages/integracoes/ContasIntegradas"));

const Assinatura = lazy(() => import("@/pages/conta/Assinatura"));
const Planos = lazy(() => import("@/pages/conta/Planos"));
const DadosCadastrais = lazy(() => import("@/pages/conta/DadosCadastrais"));
const Usuarios = lazy(() => import("@/pages/conta/Usuarios"));
const Perfil = lazy(() => import("@/pages/conta/Perfil"));

const NotFound = lazy(() => import("@/pages/NotFound"));

const ANALYTICS: SubscriptionFeature = "site-analytics";

const gated = (copy: LockedFeatureKey, page: React.ReactNode, feature = ANALYTICS) => (
  <FeatureGate feature={feature} copy={copy}>
    {page}
  </FeatureGate>
);

const publicOnly = (page: React.ReactNode) => <PublicOnlyRoute>{page}</PublicOnlyRoute>;

const PageFallback = () => <Skeleton height={320} radius={18} />;

// Entrada do painel (/dash): quem está no plano Vitrine começa na Início do
// Social; os demais, na Início geral. A assinatura já está carregada aqui
// (o DashboardLayout espera por ela).
const DashIndex = () => {
  const { subscription } = useSubscription();
  const showcasePlan = (subscription?.name ?? "").toLowerCase().includes("vitrine");
  return <Navigate to={showcasePlan ? PATHS.showcaseHome : PATHS.home} replace />;
};

// Redireciona URLs antigas de tags (/dash/analitycs/tags/:source/:tag).
const LegacyTagRedirect: React.FC = () => {
  const { trafficSource, tag } = useParams();
  if (trafficSource && tag) return <Navigate to={PATHS.tagDetail(trafficSource, tag)} replace />;
  if (trafficSource) return <Navigate to={PATHS.tagsBySource(trafficSource)} replace />;
  return <Navigate to={PATHS.tags} replace />;
};

// URLs antigas → novas (links salvos, e-mails e redirecionamentos do backend).
const LEGACY_REDIRECTS: Record<string, string> = {
  "analitycs/seu-site": PATHS.site,
  "analitycs/google-ads": PATHS.googleAds,
  "analitycs/meta-ads": PATHS.metaAds,
  "analitycs/funil-de-vendas": PATHS.funnelReports,
  "analitycs/funil-de-vendas/relatorios": PATHS.funnelReports,
  "analitycs/funil-de-vendas/formularios": PATHS.funnelForms,
  "analytics/funil-de-vendas": PATHS.funnelReports,
  "social/configuracao": PATHS.showcaseEditor,
  integracao: PATHS.trackingCode,
  "integracao/onsite": PATHS.trackingCode,
  "integracao/apps": PATHS.connectedAccounts,
  integracoes: PATHS.trackingCode,
  "minha-conta": PATHS.subscription,
  "minha-conta/plano": PATHS.subscription,
  "minha-conta/produtos": PATHS.plans,
  "minha-conta/gerenciar-conta": PATHS.accountData,
};

const AppRoutes: React.FC = () => (
  <Suspense fallback={<FullScreenLoader />}>
    <Routes>
      <Route path="/" element={<Navigate to={PATHS.dash} replace />} />

      <Route path={PATHS.signIn} element={publicOnly(<Login />)} />
      <Route path={PATHS.signUp} element={publicOnly(<Cadastro variant="signup" />)} />
      <Route path={PATHS.partners} element={publicOnly(<Cadastro variant="partners" />)} />
      <Route path={PATHS.forgotPassword} element={publicOnly(<EsqueciSenha />)} />
      <Route path={PATHS.newPassword} element={publicOnly(<NovaSenha />)} />
      <Route path={PATHS.paymentSuccess} element={<PagamentoConfirmado />} />
      <Route path={PATHS.oauthGoogleLogin} element={<GoogleLoginCallback />} />
      <Route path={PATHS.oauthGoogleAds} element={<OAuthCallback provider="google" />} />
      <Route path={PATHS.oauthMeta} element={<OAuthCallback provider="meta" />} />
      <Route path={PATHS.publicShowcase} element={<VitrinePublica />} />

      <Route
        path={PATHS.hireAccount}
        element={
          <ProtectedRoute>
            <ContratarConta />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dash"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashIndex />} />
        {/* Suspense interno: troca só o conteúdo, mantendo topbar e menu. */}
        <Route
          element={
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route path="inicio" element={<Inicio />} />
          <Route path="visao-geral" element={gated("overview", <VisaoGeral />)} />
          <Route path="gerador-de-link" element={gated("linkGenerator", <GeradorLink />)} />

          <Route path="analytics/seu-site" element={gated("site", <SeuSite />)} />
          <Route path="analytics/google-ads" element={gated("googleAds", <GoogleAds />)} />
          <Route path="analytics/meta-ads" element={gated("metaAds", <MetaAds />)} />
          <Route path="analytics/tags" element={gated("tags", <Tags />)} />
          <Route path="analytics/tags/:trafficSource" element={gated("tags", <TagsCanal />)} />
          <Route path="analytics/tags/:trafficSource/:tag" element={gated("tags", <TagDetalhe />)} />
          <Route path="analytics/funil-de-vendas/relatorios" element={gated("funnel", <FunilRelatorios />)} />
          <Route path="analytics/funil-de-vendas/formularios" element={gated("funnel", <FunilFormularios />)} />

          <Route path="social" element={<VitrineInicio />} />
          <Route path="social/cabecalho" element={<VitrineCabecalho />} />
          <Route path="social/editor" element={<VitrineEditor />} />
          <Route path="social/temas" element={<VitrineTemas />} />
          <Route path="social/relatorios" element={<VitrineRelatorios />} />

          <Route
            path="integracoes/codigo"
            element={
              <RequireRole roles={["account-admin", "analyst"]}>
                {gated("integrations", <CodigoMonitoramento />)}
              </RequireRole>
            }
          />
          <Route
            path="integracoes/contas"
            element={
              <RequireRole roles={["account-admin"]}>
                {gated("integrations", <ContasIntegradas />)}
              </RequireRole>
            }
          />

          <Route path="minha-conta/assinatura" element={<Assinatura />} />
          <Route
            path="minha-conta/planos"
            element={
              <RequireRole roles={["account-admin", "finance-manager"]}>
                <Planos />
              </RequireRole>
            }
          />
          <Route
            path="minha-conta/dados-cadastrais"
            element={
              <RequireRole roles={["account-admin", "finance-manager"]}>
                <DadosCadastrais />
              </RequireRole>
            }
          />
          <Route
            path="minha-conta/usuarios"
            element={
              <RequireRole roles={["account-admin"]}>
                <Usuarios />
              </RequireRole>
            }
          />
          <Route path="minha-conta/perfil" element={<Perfil />} />

          {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="analitycs/tags" element={<LegacyTagRedirect />} />
          <Route path="analitycs/tags/:trafficSource" element={<LegacyTagRedirect />} />
          <Route path="analitycs/tags/:trafficSource/:tag" element={<LegacyTagRedirect />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
